'use client';

import React from 'react';
import { LayoutGrid, Zap, Sparkles, Tv, Gamepad2, Star, Ticket, Ghost, Rocket, Laugh, Drama, BookOpen, Mic2, Scissors, Car, Leaf, Trophy, Music, Flame, Clapperboard, Wand2, Layers, Play, FileVideo, Volume2, LayoutTemplate, Film,  } from 'lucide-react';

/**
 * Canonical category icon map.
 * Keys are lowercase, normalised category identifiers.
 * Each entry has: icon component, display label, and a brand color.
 */
export const CATEGORY_ICON_MAP: Record<
  string,
  { icon: React.ElementType; label: string; color: string }
> = {
  // ── Content-type categories (upload types) ──────────────────────────────
  scenepacks: { icon: Film,         label: 'Scenepacks',   color: '#6C63FF' },
  scenepack:  { icon: Film,         label: 'Scenepack',    color: '#6C63FF' },
  'clip bundles': { icon: Layers,    label: 'Clip Bundles', color: '#3B9EFF' },
  'clip bundle':  { icon: Layers,    label: 'Clip Bundle',  color: '#3B9EFF' },
  clips:      { icon: Play,          label: 'Clips',        color: '#3B9EFF' },
  edits:      { icon: Scissors,      label: 'Edits',        color: '#F59E0B' },
  sounds:     { icon: Volume2,       label: 'Sounds',       color: '#22C55E' },
  templates:  { icon: LayoutTemplate,label: 'Templates',    color: '#EC4899' },

  // ── Genre / topic categories ────────────────────────────────────────────
  all:         { icon: LayoutGrid,   label: 'All',          color: 'var(--muted-foreground)' },
  action:      { icon: Zap,          label: 'Action',       color: '#EF4444' },
  anime:       { icon: Sparkles,     label: 'Anime',        color: '#A855F7' },
  movies:      { icon: Clapperboard, label: 'Movies',       color: '#F97316' },
  tvshows:     { icon: Tv,           label: 'TV Shows',     color: '#06B6D4' },
  'tv shows':  { icon: Tv,           label: 'TV Shows',     color: '#06B6D4' },
  gaming:      { icon: Gamepad2,     label: 'Gaming',       color: '#10B981' },
  celebrities: { icon: Star,         label: 'Celebrities',  color: '#FBBF24' },
  trailers:    { icon: Ticket,       label: 'Trailers',     color: '#F43F5E' },
  horror:      { icon: Ghost,        label: 'Horror',       color: '#6B7280' },
  scifi:       { icon: Rocket,       label: 'Sci-Fi',       color: '#60A5FA' },
  'sci-fi':    { icon: Rocket,       label: 'Sci-Fi',       color: '#60A5FA' },
  comedy:      { icon: Laugh,        label: 'Comedy',       color: '#FCD34D' },
  drama:       { icon: Drama,        label: 'Drama',        color: '#C084FC' },
  documentary: { icon: BookOpen,     label: 'Documentary',  color: '#34D399' },
  kpop:        { icon: Mic2,         label: 'K-Pop',        color: '#F472B6' },
  'k-pop':     { icon: Mic2,         label: 'K-Pop',        color: '#F472B6' },
  cars:        { icon: Car,          label: 'Cars',         color: '#94A3B8' },
  nature:      { icon: Leaf,         label: 'Nature',       color: '#4ADE80' },
  sports:      { icon: Trophy,       label: 'Sports',       color: '#FB923C' },
  music:       { icon: Music,        label: 'Music Videos', color: '#E879F9' },
  'music videos': { icon: Music,     label: 'Music Videos', color: '#E879F9' },
  memes:       { icon: Flame,        label: 'Memes',        color: '#FF6B35' },
  cinematic:   { icon: Clapperboard, label: 'Cinematic',    color: '#818CF8' },
  vfx:         { icon: Wand2,        label: 'VFX',          color: '#38BDF8' },
};

/** Normalise a raw category string to a lookup key */
function normalise(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Resolve icon entry for any category string */
export function getCategoryEntry(category: string) {
  const key = normalise(category);
  return CATEGORY_ICON_MAP[key] ?? { icon: FileVideo, label: category, color: 'var(--muted-foreground)' };
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface CategoryIconProps {
  /** Raw category name, e.g. "Anime", "TV Shows", "Scenepacks" */
  category: string;
  /** Icon pixel size (default 14) */
  size?: number;
  /** Override icon colour (defaults to category brand colour) */
  color?: string;
  /** Extra className */
  className?: string;
}

/**
 * Renders the canonical icon for a given category.
 * Drop-in replacement wherever a category icon is needed.
 */
export default function CategoryIcon({ category, size = 14, color, className }: CategoryIconProps) {
  const entry = getCategoryEntry(category);
  const IconComponent = entry.icon;
  return (
    <IconComponent
      size={size}
      className={className}
      style={{ color: color ?? entry.color, flexShrink: 0 }}
    />
  );
}

// ── CategoryLabel ──────────────────────────────────────────────────────────────

interface CategoryLabelProps {
  /** Raw category name */
  category: string;
  /** Icon size (default 14) */
  iconSize?: number;
  /** Text size class (default "text-xs") */
  textSize?: string;
  /** Gap between icon and text (default "gap-1.5") */
  gap?: string;
  /** Override icon colour */
  iconColor?: string;
  /** Extra className on the wrapper */
  className?: string;
  /** Show icon before (default) or after text */
  iconPosition?: 'before' | 'after';
}

/**
 * Renders an icon + category name inline, consistently styled.
 * Use this wherever a category name appears in the UI.
 */
export function CategoryLabel({
  category,
  iconSize = 14,
  textSize = 'text-xs',
  gap = 'gap-1.5',
  iconColor,
  className = '',
  iconPosition = 'before',
}: CategoryLabelProps) {
  const entry = getCategoryEntry(category);
  const IconComponent = entry.icon;
  const resolvedColor = iconColor ?? entry.color;

  const icon = (
    <IconComponent
      size={iconSize}
      style={{ color: resolvedColor, flexShrink: 0 }}
    />
  );

  return (
    <span className={`inline-flex items-center ${gap} ${className}`}>
      {iconPosition === 'before' && icon}
      <span className={`${textSize} font-medium`}>{entry.label || category}</span>
      {iconPosition === 'after' && icon}
    </span>
  );
}
